import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { dateToUtcDate, utcDateToLocalDate } from "../../../../../utils/Helper";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { includes, isEmpty } from "lodash";
import moment from "moment";
import { Sidebar } from "primereact/sidebar";
import { Col, Row } from "react-bootstrap";
import { InputTextAreaField } from "../../../../../components/Shared/InputTextAreaField";
import { Button } from "primereact/button";
import InputDateTime from "../../../../../components/Shared/InputDateTime";
import { Divider } from "primereact/divider";
import AlpOutboundLoadDocument from "../AlpOutboundLoad/AlpOutboundLoadDocument";
import GroupedLoadsByRoute from "./GroupedLoadsByRoute";

interface IFormProps {
    id: string | null; // groupToken
    t: any | null;
    onRefresh?: any | null;
    dropdowns?: any | null;
    onClose?: any | null;
    toastify?: any | null;
}

type TInputForm = {
    date?: any;
    comments?: any;
};

const DEFAULT_FORM_STATE: TInputForm = {
    date: null,
    comments: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        date: yup.date(t("the field value is not valid", { field: t('date') }))
            .required(t('the field is required.', { field: t('date') })).nullable(),
        comments: yup.string().nullable(),
    });
};

function AlpOutboundLoadGroupUnloadRequest(props: IFormProps) {
    const { dropdowns, toastify } = props;
    const { t } = useTranslation();
    const formRef: any = useRef();
    const documentRef: any = useRef();
    const [entity, setEntity] = useState<any>(null);
    const [initFormState, setInitFormState] = useState<any>({ ...DEFAULT_FORM_STATE });
    const [panelState, setPanelState] = useState<boolean>(false);
    const [documentPanelState, setDocumentPanelState] = useState<boolean>(false);
    const [documentErrors, setDocumentErrors] = useState<any>(null);

    const onClose = () => {
        setPanelState(false);
        setDocumentPanelState(false);
        documentRef.current?.resetDocuments();
        setDocumentErrors(null);
        if (props.onClose) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        const documents = documentRef.current?.getDocuments() || [];
        setDocumentErrors(null);
        const loads = Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];
        const eligibleLoadIds = loads
            .filter((load: any) => (
                includes(['in-transit'], load?.status)
                && !isEmpty(load?.gate_out_confirmed_at)
                && !isEmpty(load?.supplier_compound)
            ))
            .map((load: any) => load?.id)
            .filter((id: any) => !isEmpty(id));

        if ( eligibleLoadIds.length <= 0 ) {
            toastify && toastify(t("action not allowed: 404"), "error");
            return;
        }

        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ props.id }/unload/request`,
                data: {
                    ...payloads,
                    load_ids: eligibleLoadIds,
                    date: (payloads.date
                        ? dateToUtcDate(payloads.date, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null),
                    documents: documents.map((item: any) => ({
                        id: item.document_id,
                        title: item.title
                    }))
                },
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            resetForm();
            onClose();

            if (props.onRefresh) {
                props.onRefresh();
            }
        } catch (e: any) {
            if (e.status === 422) {
                if (e.data && e.data.errors && e.data.errors.damage_documents) {
                    setDocumentErrors(e.data.errors.damage_documents);
                } else {
                    setErrors(e.data.errors);
                }
            } else if (e.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    const getDetail = async (groupToken: string) => {
        try {
            const response = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });
            const data = response?.data?.data ?? response?.data;
            const firstLoad = Array.isArray(data?.alp_outbound_loads) && data.alp_outbound_loads.length > 0
                ? data.alp_outbound_loads[0]
                : null;
            setPanelState(true);

            setTimeout(() => {
                setEntity(data);
                setInitFormState({
                    ...DEFAULT_FORM_STATE,
                    date: firstLoad?.delivered_at
                        ? utcDateToLocalDate(firstLoad.delivered_at, 'YYYY-MM-DD HH:mm:ss')
                        : moment().format('YYYY-MM-DD HH:mm:ss'),
                });
                documentRef.current?.resetDocuments();
                setDocumentErrors(null);
            }, 250);
        } catch (error: any) {
            toastify && toastify(t("server error"), "error");
        }
    };

    useEffect(() => {
        setEntity(null);
        if (!isEmpty(props.id)) {
            // @ts-ignore
            getDetail(props.id);
        }
    }, [props.id]);

    const loads = Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];
    const firstGroupedLoad = loads.length > 0 ? loads[0] : null;
    const providerUploadSlug = entity?.provider_slug
        ?? entity?.provider?.slug
        ?? firstGroupedLoad?.provider_slug
        ?? firstGroupedLoad?.provider?.slug
        ?? firstGroupedLoad?.source_type
        ?? entity?.source_type
        ?? 'emil_frey';

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('unload request') }</h5>
                            <p className="mb-0">
                                <strong>{ t("group token") }:</strong> { props.id }
                            </p>
                        </div>
                    }
                    visible={ panelState }
                    position="right"
                    onHide={ onClose }
                    blockScroll={ true }
                    style={ { width: '800px' } }
                    closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (fp: FormikProps<TInputForm>) => (
                            <Form onSubmit={ fp.handleSubmit }>
                                { entity && (
                                    <Row className={ "mt-4 mb-7" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                placeholder={ t("pick a date") }
                                                component={ InputDateTime }
                                                name={ `date` }
                                                type={ "datetime" }
                                                label={ t("date") }
                                                maxDate={ moment().endOf('day').tz(moment.tz.guess()).toDate() }
                                                isRequired
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 12 }>
                                            <Field
                                                component={ InputTextAreaField }
                                                name="comments"
                                                label={ t('comments') }
                                                placeholder={ t("enter") + ' ' + t("comments").toLowerCase() }
                                            />
                                        </Col>

                                        <Col sm={ 12 } className={ "mt-3" }>
                                            <Divider />
                                            { documentErrors && (
                                                <div className="mt-2">
                                                    { documentErrors.map((error: string, index: number) => (
                                                        <div key={ index } className="text-danger small">
                                                            { error }
                                                        </div>
                                                    )) }
                                                </div>
                                            ) }
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">{ t('document upload') }</h6>
                                                <Button
                                                    type="button"
                                                    size="small"
                                                    outlined
                                                    severity={ documentPanelState ? "secondary" : "success" }
                                                    onClick={ () => setDocumentPanelState(!documentPanelState) }
                                                    tooltip={ documentPanelState ? t('hide documents') : t('show documents') }
                                                    tooltipOptions={ { position: 'top' } }
                                                >
                                                    <i className={ `pi ${ documentPanelState ? 'pi-eye-slash' : 'pi-eye' } me-2` } />
                                                    { t('document upload') }
                                                </Button>
                                            </div>
                                            { documentPanelState && (
                                                <AlpOutboundLoadDocument
                                                    ref={ documentRef }
                                                    dropdowns={ dropdowns }
                                                    providerUploadSlug={ providerUploadSlug }
                                                />
                                            ) }
                                        </Col>

                                        <Col sm={ 12 } md={ 12 } className={ "mb-5" }>
                                            <hr />
                                            <h4 className="card-title">{ t('loads') }</h4>
                                            <GroupedLoadsByRoute loads={ loads } t={ t } />
                                        </Col>
                                    </Row>
                                ) }

                                <div className="filter-card-footer" style={ { width: '785px' } }>
                                    <Button
                                        tooltip={ t("to data save into database") }
                                        size={ "small" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        disabled={ fp.isSubmitting }
                                    >
                                        <i className="pi pi-save me-2" />
                                        { t("let's save") }
                                    </Button>
                                    <Button
                                        type={ "button" }
                                        tooltip={ t("operation cancel") }
                                        className={ "ms-2" }
                                        size={ "small" }
                                        severity={ "danger" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        onClick={ onClose }
                                        disabled={ fp.isSubmitting }
                                    >
                                        <i className="pi pi-arrow-left me-2" />
                                        { t("cancel") }
                                    </Button>
                                </div>
                            </Form>
                        ) }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

AlpOutboundLoadGroupUnloadRequest.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupUnloadRequest);

